import React, { useCallback, useState } from 'react';
import { Button, IconButton, Icon } from '@socialchorus/shared-ui-components';
import { Redirect } from 'react-router-dom';
import useScreenTitle from '../../common/use-screen-title';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import { useChatbotThreadListQuery } from '../../common/queries/use-chatbot-query';
import { ChatbotThread } from '../../models/chatbot/types';
import { Spinner } from '../../components/ui';
import ViewTrigger from '../../components/view-trigger';
import { timeAgoOrDate } from '../../lib/date-formatter';
import useFeatureFlag from '../../common/use-feature-flag';
import { Feature } from '../../models/features/features';

import Chatbot from '../../components/chatbot/Chatbot';
import '../../components/chatbot/chatbot.scss';

// Internal ThreadItem component (not exported)
interface ThreadItemProps {
  thread: ChatbotThread;
  isActive: boolean;
  onClick: () => void;
}

const ThreadItem: React.FC<ThreadItemProps> = ({
  thread,
  isActive,
  onClick,
}) => {
  return (
    <div
      className={`chatbot__thread-item ${
        isActive ? 'chatbot__thread-item--active' : ''
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Select conversation: ${thread.title}`}
    >
      <div className="chatbot__thread-title">{thread.title}</div>
      <div className="chatbot__thread-date">
        {timeAgoOrDate(thread.updatedAt.toString())}
      </div>
    </div>
  );
};

// Internal ChatbotThreadListSidebar component
interface ChatbotThreadListSidebarProps {
  programId: string;
  selectedThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  onNewConversation: () => void;
  onCloseSidebar: () => void;
}

const ChatbotThreadListSidebar: React.FC<ChatbotThreadListSidebarProps> = ({
  programId,
  selectedThreadId,
  onThreadSelect,
  onNewConversation,
  onCloseSidebar,
}) => {
  const {
    data: chatThreadListData,
    isLoading: threadListLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useChatbotThreadListQuery(programId);

  // Flatten all threads from all pages
  const chatThreadList = chatThreadListData?.pages.flatMap((page) => page.data);

  return (
    <div className="chatbot__sidebar">
      {/* Sidebar Header */}
      <div className="chatbot__sidebar-header">
        <div className="chatbot__sidebar-title-row">
          <h2 className="chatbot__sidebar-title">Conversations</h2>
          <IconButton
            variant="default"
            size="default"
            iconName="close"
            onClick={onCloseSidebar}
            aria-label="Close conversations sidebar"
          />
        </div>
        <Button
          variant="primary"
          size="default"
          label="New Conversation"
          onClick={onNewConversation}
          leftIcon={<Icon>add</Icon>}
          fullWidth
        />
      </div>

      {/* Conversation List */}
      <div className="chatbot__thread-list">
        {threadListLoading ? (
          <div className="chatbot__loading">
            <div className="spinner"></div>
            <div className="chatbot__loading-text">
              Loading conversations...
            </div>
          </div>
        ) : (
          <div className="chatbot__thread-list-inner">
            {chatThreadList
              ?.filter((thread) => thread && thread.id)
              .map((thread) => (
                <ThreadItem
                  key={thread.id}
                  thread={thread}
                  isActive={selectedThreadId === thread.id}
                  onClick={() => onThreadSelect(thread.id)}
                />
              ))}

            {/* Infinite Loading */}
            {hasNextPage ? (
              !isFetchingNextPage ? (
                <ViewTrigger onInview={() => fetchNextPage()} />
              ) : (
                <div className="chatbot__thread-list-loading">
                  <Spinner />
                </div>
              )
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

// Main ChatbotScreen component
const ChatbotScreen: React.FC = () => {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isThreadPersisted, setIsThreadPersisted] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  const chatbotEnabled = useFeatureFlag(Feature.CHATBOT_ENABLED);

  useScreenTitle('Chatbot');

  const programId = usePatronSelector(programSelectors.getProgramId);

  // Only redirect if explicitly disabled (not undefined during React Query loading)
  if (chatbotEnabled === false) {
    return <Redirect to="/" />;
  }

  const handleThreadSelect = (threadId: string) => {
    setSelectedThreadId(threadId);
    // When selecting an existing thread from sidebar, it's already persisted
    setIsThreadPersisted(true);
  };

  const handleNewConversation = () => {
    setSelectedThreadId(null); // This will make Chatbot create a new thread
    setIsThreadPersisted(false);
    setShouldFocusInput(true);
  };

  const handleFocusHandled = useCallback(() => setShouldFocusInput(false), []);

  const handleThreadChange = (threadId: string, persisted: boolean) => {
    setSelectedThreadId(threadId);
    setIsThreadPersisted(persisted);
  };

  return (
    <div className="chatbot-screen">
      <div className="chatbot-screen__layout">
        {sidebarOpen && (
          <ChatbotThreadListSidebar
            programId={programId as string}
            selectedThreadId={selectedThreadId}
            onThreadSelect={handleThreadSelect}
            onNewConversation={handleNewConversation}
            onCloseSidebar={() => setSidebarOpen(false)}
          />
        )}

        <div className="chatbot-screen__main">
          <div className="chatbot-screen__header">
            <div className="chatbot-screen__header-content">
              {!sidebarOpen && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="chatbot-screen__sidebar-toggle"
                  aria-label="Open conversations sidebar"
                >
                  <svg
                    className="chatbot-screen__sidebar-toggle-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              )}
              <h1 className="chatbot-screen__title">Chatbot Assistant</h1>
            </div>
          </div>

          <Chatbot
            threadId={selectedThreadId || undefined}
            isThreadPersisted={isThreadPersisted}
            onThreadChange={handleThreadChange}
            shouldFocusInput={shouldFocusInput}
            onFocusHandled={handleFocusHandled}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatbotScreen;
